import type { Pixel, Unit } from '@/data';
import { BLC } from '@/data';
import { generatePositions } from '@/http-client/renderServices/StructureService';
import { Structure } from '@/data/Structure';
import { StructureBLCParser } from '@/formats/inputSourcesParsers/StructureBLCParser';
import { StructurePageParser } from '@/formats/inputSourcesParsers/StructurePageParser';
import { StructureSVGParser } from '@/formats/inputSourcesParsers/StructureSVGParser';
import MetadataParser from '../MetadataParser';
import type { Image3DSyncFormat } from '../UnitParser';
import UnitParser from '../UnitParser';
import type { StructureFormat } from './StructureFormat';

export default class StructureParser {
  static structureFormatFromNewFormat(sourceFormat: Map<string, unknown>): StructureFormat {
    return {
      structure: sourceFormat.get('structure') as string,
      info: sourceFormat.get('text') as string,
      license: sourceFormat.get('license') as string,
      contributors: sourceFormat.get('contributors') as Record<string, string>,
      id: sourceFormat.get('id') as string,
      name: sourceFormat.get('name') as string,
      year: sourceFormat.get('year') as string,
      block: sourceFormat.get('block') as string,
      line: sourceFormat.get('line') as string,
      cell: sourceFormat.get('cell') as string,
    };
  }

  static fromFormat(
    format: StructureFormat,
  ): Structure {
    const name = format?.name || format?.id;

    const id = format?.id;

    const blc = StructureBLCParser.generateBLCStructure(format);
    const positions: Image3DSyncFormat = generatePositions(blc);
    const pixelUnit: Unit<Pixel> = UnitParser.createImage3DUnit(positions);

    const pagesDimensions: { height: Pixel; width: Pixel; }[] = [];

    const pages = positions.pages
      .map((page) => StructurePageParser.createNewPage(page, pagesDimensions));

    const maxWidth = pagesDimensions
      .map((dim) => dim.width as number)
      .reduce((prev, curr) => Math.max(prev, curr));

    const totalHeight = pagesDimensions
      .map((dim) => dim.height as number)
      .reduce((prev, curr) => prev + curr + BLC.rowHeight);

    const metadata = MetadataParser.fromFormat(format);

    const oldRows = positions.pages[0].rows;
    const svgImage = StructureSVGParser.generateSvgFromRows(oldRows);
    const easterEgg = `<text id="easterEgg" x="${maxWidth}" y="${totalHeight}" >_ |</text>`;

    return new Structure({
      name,
      id,
      pixelUnit,
      width: maxWidth as Pixel,
      height: totalHeight as Pixel,
      pages,
      metadata,
      svgImage: svgImage + easterEgg,
    });
  }
}
